/**
 * useSearch.js - Cleaned up version of the original
 *
 * Reduced state complexity while keeping the same API
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cacheService } from '../services/CacheService';
import SearchService from '../services/SearchService';

const SEARCH_HISTORY_KEY = 'search_history';
const DEFAULT_DEBOUNCE_TIME = 300;
const DEFAULT_MIN_CHARS = 2;
const HISTORY_COMMIT_DELAY = 3000;

/**
 * Map user reactions from localStorage to channels
 * Search results don't have dspro_id, so we need to find it from cached appload data
 * @param {Array} channels - Array of channel objects
 * @returns {Array} Channels with user_reaction property added
 */
const mapReactionsToChannels = (channels) => {
  if (!channels || channels.length === 0) return channels;
  
  // Get stored reactions from localStorage (this is reliable and fast)
  const storedReactions = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('reaction_')) {
      const channelId = key.replace('reaction_', '');
      try {
        const reaction = JSON.parse(localStorage.getItem(key));
        if (reaction) {
          storedReactions[channelId] = reaction;
        }
      } catch (e) {
        console.warn('Failed to parse stored reaction for', channelId, e);
      }
    }
  }
  
  
  // Get cached appload data to find dspro_id mappings
  // Check new prefix first, then fall back to old prefix for backward compatibility
  let cachedAppLoad = null;
  try {
    let cachedAppLoadRaw = localStorage.getItem('electricnow_cache_SPOTLIGHT_GET_APP_LOAD');
    if (!cachedAppLoadRaw) {
      cachedAppLoadRaw = localStorage.getItem('chillfree_cache_SPOTLIGHT_GET_APP_LOAD');
    }
    if (cachedAppLoadRaw) {
      const cacheItem = JSON.parse(cachedAppLoadRaw);
      cachedAppLoad = cacheItem.data;
    }
  } catch (e) {
    console.warn('🔍 Failed to parse cached app load data:', e);
  }
  
  // Create a mapping from _id to dspro_id using cached data
  const idMappings = {};
  if (cachedAppLoad) {
    const homepageData = cachedAppLoad?.data?.homepage || cachedAppLoad?.homepage || cachedAppLoad;
    if (homepageData && Array.isArray(homepageData)) {
      for (const category of homepageData) {
        if (category.channels) {
          for (const channel of category.channels) {
            if (channel._id && channel.dspro_id) {
              idMappings[channel._id] = channel.dspro_id;
            }
          }
        }
      }
    }
  }
  
  
  // Map reactions to channels
  return channels.map(channel => {
    // For search results, try to find the dspro_id from our mapping
    const searchId = channel._id;
    const dspro_id = idMappings[searchId];
    
    // Try both the mapped dspro_id and the original _id
    const possibleIds = [dspro_id, searchId].filter(Boolean);
    let userReaction = null;
    let matchedId = null;
    
    for (const id of possibleIds) {
      if (storedReactions[id]) {
        userReaction = storedReactions[id];
        matchedId = id;
        break;
      }
    }
    
    
    // Handle both string reactions ('like') and object reactions ({action: 'like'})
    const normalizedReaction = typeof userReaction === 'string' ? userReaction : userReaction?.action || userReaction;
    
    return {
      ...channel,
      dspro_id, // Add the dspro_id we found from the mapping
      user_reaction: normalizedReaction || null
    };
  });
};

export const useSearch = (options = {}) => {
  const {
    initialQuery = '',
    debounceTime = DEFAULT_DEBOUNCE_TIME,
    autoCompleteMinChars = DEFAULT_MIN_CHARS,
    platform = 'website',
    autoFocus = false,
    enableHistory = true,
    maxHistoryItems = 10,
    enableRealTimeResults = true,
  } = options;

  // Core state - reduced from 17 to 7 essential pieces
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [textSuggestions, setTextSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRealTimeSearch, setIsRealTimeSearch] = useState(false);

  // Refs for cleanup
  const navigate = useNavigate();
  const location = useLocation();
  const searchTimeoutRef = useRef(null);
  const suggestionsTimeoutRef = useRef(null);
  const historyTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const suggestionsAbortControllerRef = useRef(null);

  // Auto-focus if enabled
  useEffect(() => {
    if (autoFocus && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [autoFocus]);

  // Initialize from URL once on mount only
  useEffect(() => {
    if (location.pathname === '/search' && !query) {
      // Only if query is empty
      const params = new URLSearchParams(location.search);
      const urlQuery = params.get('q') || '';
      if (urlQuery) {
        setQuery(urlQuery);
        if (urlQuery.trim()) {
          performSearch(urlQuery, false, false);
        }
      }
    }
  }, []); // Empty dependency array - only run on mount

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (suggestionsTimeoutRef.current) clearTimeout(suggestionsTimeoutRef.current);
      if (historyTimeoutRef.current) clearTimeout(historyTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (suggestionsAbortControllerRef.current) suggestionsAbortControllerRef.current.abort();
    };
  }, []);

  // Search history state
  const [searchHistory, setSearchHistory] = useState([]);

  // Load history on mount
  useEffect(() => {
    if (enableHistory) {
      const history = cacheService.get(SEARCH_HISTORY_KEY) || [];
      setSearchHistory(history);
    }
  }, [enableHistory]);

  const addToSearchHistory = useCallback(
    searchQuery => {
      if (!enableHistory || !searchQuery.trim()) return;

      const newHistory = [searchQuery, ...searchHistory.filter(item => item !== searchQuery)].slice(
        0,
        maxHistoryItems,
      );

      setSearchHistory(newHistory);
      cacheService.set(SEARCH_HISTORY_KEY, newHistory, 60);
    },
    [enableHistory, maxHistoryItems, searchHistory],
  );

  const clearSearchHistory = useCallback(() => {
    setSearchHistory([]);
    cacheService.invalidate(SEARCH_HISTORY_KEY);
  }, []);

  // Main search function
  const performSearch = useCallback(
    async (searchQuery = query, addToHistory = true, updateURL = true) => {
      if (!searchQuery.trim()) {
        setResults([]);
        setError(null);
        setIsRealTimeSearch(false);
        return;
      }

      setLoading(true);
      setError(null);

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      try {
        const searchOptions = {
          signal: abortControllerRef.current.signal,
        };

        const searchData = await SearchService.searchChannels(searchQuery, platform, searchOptions);
        
        
        // Map user reactions to search results
        const resultsWithReactions = mapReactionsToChannels(searchData.channels || []);
        
        
        setResults(resultsWithReactions);

        if (addToHistory) {
          addToSearchHistory(searchQuery);
        }

        if (updateURL) {
          const params = new URLSearchParams();
          params.set('q', searchQuery);
          navigate(`/search?${params.toString()}`, { replace: true });
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Search error:', err);
          setError(err.message || 'Search failed. Please try again.');
          setResults([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [query, platform, navigate, addToSearchHistory],
  );

  // Get suggestions
  const getSuggestions = useCallback(
    async searchQuery => {
      // getSuggestions called

      if (!searchQuery.trim() || searchQuery.length < autoCompleteMinChars) {
        // Query too short or empty, clearing suggestions
        setTextSuggestions([]);
        return;
      }

      // Cancel previous suggestions request
      if (suggestionsAbortControllerRef.current) {
        suggestionsAbortControllerRef.current.abort();
      }

              // Starting suggestions request
      setSuggestionsLoading(true);
      suggestionsAbortControllerRef.current = new AbortController();

      try {
        const suggestionsData = await SearchService.getChannelSuggestions(searchQuery, {
          signal: suggestionsAbortControllerRef.current.signal,
        });

                  // Suggestions response received
        const suggestions = suggestionsData.channels?.map(channel => channel.title) || [];
                  // Processed suggestions
        setTextSuggestions(suggestions);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('[useSearch] Error fetching suggestions:', err);
          setTextSuggestions([]);
        }
      } finally {
        setSuggestionsLoading(false);
      }
    },
    [autoCompleteMinChars],
  );

  // Handle search input change
  const handleSearchChange = useCallback(
    e => {
      const newQuery = e.target.value;
      setQuery(newQuery);

      // Clear timeouts
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (suggestionsTimeoutRef.current) clearTimeout(suggestionsTimeoutRef.current);
      if (historyTimeoutRef.current) clearTimeout(historyTimeoutRef.current);

      // Handle empty query
      if (!newQuery.trim()) {
        setResults([]);
        setTextSuggestions([]);
        setIsRealTimeSearch(false);
        setError(null);
        navigate('/search', { replace: true });
        return;
      }

      setIsRealTimeSearch(true);

      // Get suggestions (faster)
      if (newQuery.length >= autoCompleteMinChars) {
        suggestionsTimeoutRef.current = setTimeout(() => {
          getSuggestions(newQuery);
        }, debounceTime / 3);
      }

      // Get search results
      if (enableRealTimeResults && newQuery.length >= autoCompleteMinChars) {
        searchTimeoutRef.current = setTimeout(() => {
          performSearch(newQuery, false, true);
        }, debounceTime);
      }

      // Add to history after user stops typing
      if (newQuery.trim()) {
        historyTimeoutRef.current = setTimeout(() => {
          addToSearchHistory(newQuery);
        }, HISTORY_COMMIT_DELAY);
      }
    },
    [
      debounceTime,
      autoCompleteMinChars,
      enableRealTimeResults,
      performSearch,
      getSuggestions,
      addToSearchHistory,
      navigate,
    ],
  );

  // Handle form submission
  const handleSearchSubmit = useCallback(
    e => {
      e.preventDefault();
      setIsRealTimeSearch(false);
      performSearch(query, true, true);
    },
    [query, performSearch],
  );

  // Select suggestion
  const selectSuggestion = useCallback(
    suggestion => {
      setQuery(suggestion);
      setIsRealTimeSearch(false);

      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }

      performSearch(suggestion, true, true);
    },
    [performSearch],
  );

  // Clear search
  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setTextSuggestions([]);
    setError(null);
    setIsRealTimeSearch(false);
    navigate('/search', { replace: true });

    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [navigate]);

  return {
    // State
    query,
    results,
    textSuggestions,
    loading,
    suggestionsLoading,
    error,
    isRealTimeSearch,

    // Refs
    searchInputRef,

    // Methods
    handleSearchChange,
    handleSearchSubmit,
    selectSuggestion,
    clearSearch,
    performSearch,
    addToSearchHistory,
    clearSearchHistory,

    // History
    searchHistory,

    // Simple computed values (no useMemo needed for these)
    hasResults: results.length > 0,
    hasQuery: query.trim().length > 0,
  };
};
